import React, { useState, useEffect, useMemo } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  Box, 
  TextField, 
  InputAdornment,
  ListSubheader,
  OutlinedInput,
  Chip,
  Tooltip,
  Typography
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { POSITION_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { COLORS } from '../../../constants/radar';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = POSITION_RETURN_TYPES;

const PositionSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  onPositionChange, // Optional callback that receives (value, positionData)
  onSelectedPositionChange, // Optional callback that receives the full selected position object
  label = "Position",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = POSITION_RETURN_TYPES.ID, // POSITION_RETURN_TYPES.ID or POSITION_RETURN_TYPES.NAME
  // Ids to leave OUT of the list. Added for ADR-096's policy page, where a
  // position that already has an override must not be offered again — the
  // unique constraint then backs the UI up rather than surfacing as an error
  // the user has to read. Default empty: every existing caller is unaffected.
  excludeIds = [],
  // Narrow the list to ONE org unit type. Added for ADR-097's config page,
  // where positions are added inside a type group and offering the other
  // types' positions there would let someone file a rule under the wrong
  // heading. Default null: every existing caller still sees all types.
  orgUnitTypeId = null,
  // ADR-110 opt-in props (backward compatible — omit them for the classic behaviour):
  positionsByOrgUnitTypeData = undefined, // Optional: pre-built groups [{ orgUnitType|null, count?, positions }]; skips the self-fetch
  loading = false, // Optional: loading state of positionsByOrgUnitTypeData
  getItemCount = null, // Optional: function(position) => number|null, rendered right-aligned on each option
  getGroupCount = null, // Optional: function(group) => number|null, rendered on each group header
  itemCountTooltip = null, // Optional: tooltip for the counts
  emptyStateLabel = null, // Optional: text shown when there are no options (default "No positions found")
  untypedGroupLabel = 'Other positions', // Optional: header of the group whose orgUnitType is null
  footerNote = null, // Optional: disabled note rendered below the options
  // Remove open, onOpen, onClose props - let Material-UI handle dropdown state
  ...props 
}) => {
  const isProvided = positionsByOrgUnitTypeData !== undefined;
  const [fetchedPositionsByOrgUnitType, setFetchedPositionsByOrgUnitType] = useState([]);
  const [isFetching, setIsFetching] = useState(!isProvided);
  const [positionSearchQuery, setPositionSearchQuery] = useState('');
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const { showAlert } = useAlert();

  const positionsByOrgUnitType = useMemo(
    () => (isProvided ? (positionsByOrgUnitTypeData || []) : fetchedPositionsByOrgUnitType),
    [isProvided, positionsByOrgUnitTypeData, fetchedPositionsByOrgUnitType]
  );
  // Keep flat positions array for quick lookup
  const positions = useMemo(
    () => positionsByOrgUnitType.reduce((acc, group) => [...acc, ...(group.positions || [])], []),
    [positionsByOrgUnitType]
  );
  const isLoading = isProvided ? loading : isFetching;
  // Provided data refreshes in place: keep the control usable while options it already shows reload.
  const isControlDisabled = disabled || (isLoading && (!isProvided || positions.length === 0));

  useEffect(() => {
    if (isProvided) {
      return;
    }
    const fetchPositions = async () => {
      try {
        setIsFetching(true);
        const positionsData = await organizationApi.getPositionsByOrgUnitType();
        setFetchedPositionsByOrgUnitType(positionsData);
      } catch (error) {
        showAlert('Failed to load positions', 'error', error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchPositions();
  }, [showAlert, isProvided]);

  const renderCount = (count) => {
    if (count === null || count === undefined) {
      return null;
    }
    const countElement = (
      <Typography
        component="span"
        sx={{
          ml: 'auto',
          pl: 2,
          fontSize: '0.8rem',
          fontWeight: 600,
          color: count > 0 ? '#475569' : '#94A3B8',
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {count}
      </Typography>
    );
    if (!itemCountTooltip) {
      return countElement;
    }
    return (
      <Tooltip title={itemCountTooltip} placement="right" arrow>
        {countElement}
      </Tooltip>
    );
  };

  // Notify parent when selected position changes - prevent infinite loops by limiting when this runs
  useEffect(() => {
    if (onSelectedPositionChange && positions.length > 0 && value) {
      const currentSelectedPosition = getSelectedPosition();
      // Only call if we actually have a position selected
      if (currentSelectedPosition) {
        onSelectedPositionChange(currentSelectedPosition);
      }
    }
  }, [value, positions.length]); // Remove onSelectedPositionChange from deps to prevent infinite loops

  // Auto-scroll to selected position when dropdown opens
  useEffect(() => {
    if (isSelectOpen && value) {
      // Give time for the select menu to render
      setTimeout(() => {
        const selectedPositionId = getSelectedPositionId();
        if (selectedPositionId) {
          const selectedElement = document.querySelector(`[data-value="${selectedPositionId}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ block: 'center' });
          }
        }
      }, 100);
    }
  }, [isSelectOpen, value]);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (multiple) {
      // Handle multiple selection
      const selectedPositions = selectedValue.map(id => positions.find(pos => pos.id === id)).filter(Boolean);
      
      if (onChange) {
        if (returnValue === POSITION_RETURN_TYPES.NAME) {
          onChange(selectedPositions.map(pos => pos.label));
        } else {
          onChange(selectedValue);
        }
      }
      
      // Call the onPositionChange callback for multiple selection
      if (onPositionChange && selectedPositions.length > 0) {
        const values = returnValue === POSITION_RETURN_TYPES.NAME ? selectedPositions.map(pos => pos.label) : selectedValue;
        onPositionChange(values, selectedPositions);
      }
    } else {
      // Handle single selection
      const selectedPosition = positions.find(pos => pos.id === selectedValue);
      
      if (onChange) {
        if (returnValue === POSITION_RETURN_TYPES.NAME) {
          onChange(selectedPosition?.label || '');
        } else {
          onChange(selectedValue);
        }
      }
      
      // Call the onPositionChange callback with both value and position data
      if (onPositionChange && selectedPosition) {
        const value = returnValue === POSITION_RETURN_TYPES.NAME ? selectedPosition.label : selectedPosition.id;
        onPositionChange(value, selectedPosition);
      }
    }
  };

  // Helper function to filter positions based on search query and exclusions
  const filterPositions = (positions, query) => {
    const excluded = new Set(excludeIds || []);
    if (orgUnitTypeId) {
      positions = positions.filter((position) => position.orgUnitTypeId === orgUnitTypeId);
    }
    // Never hide what is currently selected: a value the control cannot
    // display is a control that looks broken.
    const available = excluded.size > 0
      ? positions.filter(position => !excluded.has(position.id) || position.id === value)
      : positions;
    if (!query) return available;
    return available.filter(position => 
      position.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Get filtered positions grouped by org unit type
  const getFilteredPositionsByOrgUnitType = () => {
    return positionsByOrgUnitType.map(group => ({
      ...group,
      positions: filterPositions(group.positions, positionSearchQuery)
    })).filter(group => group.positions.length > 0);
  };

  // Find the selected position object from the ID or name
  const getSelectedPosition = () => {
    return positions.find(position => {
      if (returnValue === POSITION_RETURN_TYPES.NAME) {
        return position.label === value;
      } else {
        return position.id === value;
      }
    }) || null;
  };

  // Get the internal ID(s) for the selected position(s) (always use ID internally)
  const getSelectedPositionId = () => {
    if (multiple) {
      if (!value || !Array.isArray(value) || value.length === 0) return [];

      if (returnValue === POSITION_RETURN_TYPES.NAME) {
        // Convert names to IDs
        return value.map(name => {
          const position = positions.find(p => p.label === name);
          return position?.id;
        }).filter(Boolean);
      } else {
        // Already IDs
        return value;
      }
    } else {
      const selectedPosition = getSelectedPosition();
      return selectedPosition?.id || '';
    }
  };



  return (
    <FormControl fullWidth={fullWidth} required={required} disabled={isControlDisabled} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={getSelectedPositionId()}
        onChange={handleChange}
        label={label}
        disabled={isControlDisabled}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={multiple ? (selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((positionId) => {
              const position = positions.find(p => p.id === positionId);
              return (
                <Chip 
                  key={positionId} 
                  label={position?.label || `Position ${positionId}`}
                  size="small"
                  sx={{ 
                     backgroundColor: COLORS.PRIMARY,
                     color: 'white',
                     height: '24px',
                     fontSize: '0.75rem',
                     '.MuiChip-deleteIcon': {
                       color: 'white',
                       fontSize: '16px',
                       '&:hover': { color: COLORS.SECONDARY }
                     }
                   }}
                />
              );
            })}
          </Box>
        ) : undefined}
        onClose={() => {
          setPositionSearchQuery('');
          setIsSelectOpen(false);
        }}
        onOpen={() => {
          setIsSelectOpen(true);
        }}
        endAdornment={
          isLoading ? <CircularProgress color="inherit" size={20} /> : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 450,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          },
          autoFocus: false,
          disableAutoFocusItem: true
        }}
      >
        <Box 
          sx={{ 
            position: 'sticky', 
            top: 0, 
            bgcolor: 'background.paper', 
            zIndex: 1,
            p: 1
          }}
        >
          <TextField
            size="small"
            placeholder="Search positions..."
            value={positionSearchQuery}
            onChange={(e) => setPositionSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              // Prevent select from handling keyboard events
              e.stopPropagation();
            }}
            autoComplete="off"
            sx={{ 
              width: '100%',
              backgroundColor: 'white',
              '& .MuiInputBase-root': {
                borderRadius: 1
              }
            }}
            slots={{
              startAdornment: () => (
                <InputAdornment position="start">
                  <FaSearch size={14} />
                </InputAdornment>
              ),
            }}
            autoFocus
          />
        </Box>
        {getFilteredPositionsByOrgUnitType().map(group => [
          <ListSubheader
            key={group.orgUnitType?.id || 'untyped-positions'}
            sx={{
              backgroundColor: '#f8f9fa',
              lineHeight: '32px',
              borderRadius: '4px',
              color: '#6b7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              position: 'static' // Prevent sticky behavior of subheader
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <span>{group.orgUnitType ? group.orgUnitType.name : untypedGroupLabel}</span>
              {getGroupCount && renderCount(getGroupCount(group))}
            </Box>
          </ListSubheader>,
          ...group.positions.map(position => (
            <MenuItem 
              key={position.id} 
              value={position.id}
              sx={{
                pl: 4 // Add indentation for positions under org unit type
              }}
            >
              {getItemCount ? (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <span>{position.label}</span>
                  {renderCount(getItemCount(position))}
                </Box>
              ) : position.label}
            </MenuItem>
          ))
        ])}
        {getFilteredPositionsByOrgUnitType().length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            {positionSearchQuery || !emptyStateLabel ? 'No positions found' : emptyStateLabel}
          </Box>
        )}
        {footerNote && (
          <Box sx={{ px: 2, py: 1, fontSize: '0.8rem', color: 'text.secondary', borderTop: '1px solid #e5e7eb' }}>
            {footerNote}
          </Box>
        )}
      </Select>
    </FormControl>
  );
};

export default PositionSelector; 